"use client";

import React, { useEffect, useRef, useState } from "react";
import { RegularButton, ButtonSize, ButtonVariant } from "@/stories/button/button";
import Info from "@/app/assets/icons/info";
import LoadingIcon from "@/app/assets/icons/loading";
import { SIGNUP_STEPS } from "@/app/enums/signup";
import ConsentDisclaimer from "@/app/components/shared/consent-disclaimer";

declare global {
    interface CredentialRequestOptions {
        otp?: OTPOptions;
    }

    interface OTPOptions {
        transport: string[];
    }

    interface Credential {
        code?: string;
    }
}

interface OtpInputProps {
    handlePhoneOtp: (otp: string) => void;
    handleEmailOtp: (otp: string) => void;
    resetOtp: boolean;
    phoneNumber: string;
    editPhoneNumber: () => void;
    editEmailId: () => void;
    email: string;
    otpType: string;
    resendOtp: (type: string) => void;
    otpErrorMessage: string | null;
    setOtpErrorMessage: (msg: string) => void;
    buttonLoader: boolean;
    enableAutoSubmit?: boolean; // Flag to enable/disable auto submission
    enableAutoRead?: boolean;   // Flag to enable/disable auto OTP reading
}

const OtpInput: React.FC<OtpInputProps> = ({
    handlePhoneOtp,
    handleEmailOtp,
    resetOtp,
    phoneNumber,
    editPhoneNumber,
    otpType,
    email,
    editEmailId,
    resendOtp,
    otpErrorMessage,
    setOtpErrorMessage,
    buttonLoader,
    enableAutoSubmit = false, // Default to false (disabled)
    enableAutoRead = false,   // Default to false (disabled)
}) => {
    const [otp, setOtp] = useState("");
    const [seconds, setSeconds] = useState(30);
    const [isResendEnabled, setIsResendEnabled] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const verifyOTP = useRef<HTMLButtonElement | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // WebOTP API (Android) - Only if auto-read is enabled
    useEffect(() => {
        if (
            !enableAutoRead ||
            otpType !== SIGNUP_STEPS.PHONEOTP ||
            !window.isSecureContext ||
            !("OTPCredential" in window)
        )
            return;

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        const receiveOTP = async () => {
            try {
                const content = (await navigator.credentials.get({
                    otp: { transport: ["sms"] },
                    signal,
                })) as Credential | null;

                const otpCode = content?.code ?? "";
                if (/^\d{4}$/.test(otpCode)) {
                    setOtp(otpCode);

                    // Auto-submit after WebOTP autofill only if enableAutoSubmit is true
                    if (enableAutoSubmit) {
                        setTimeout(() => verifyOTP.current?.click(), 500);
                    }
                }
            } catch (err) {
                console.log("WebOTP error:", err);
            }
        };

        receiveOTP();

        return () => abortControllerRef.current?.abort();
    }, [otpType, enableAutoRead, enableAutoSubmit]);

    // iOS/Safari: Focus input for `autocomplete="one-time-code"` - Only if auto-read is enabled
    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
    }, []); // Runs once on mount

    // Focus input when error appears
    useEffect(() => {
        if (otpErrorMessage) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [otpErrorMessage]);

    useEffect(() => {
        if (resetOtp) setOtp("");
    }, [resetOtp]);

    useEffect(() => {
        if (seconds > 0) {
            const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setIsResendEnabled(true);
        }
    }, [seconds]);

    // Auto-submit useEffect when OTP length is 4 - Only if enableAutoSubmit is true
    useEffect(() => {
        if (enableAutoSubmit && otp.trim().length === 4) {
            const timer = setTimeout(() => {
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                handleOtpVerifyClick(otpType);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [otp, enableAutoSubmit, otpType]);

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Clear error instantly when user types again
        if (otpErrorMessage) {
            setOtpErrorMessage("");
        }

        const value = e.target.value.replace(/\D/g, "").slice(0, 4); // only numbers, max 4
        setOtp(value);

        // Auto-submit when OTP length is 4 - Only if enableAutoSubmit is true
        if (enableAutoSubmit && value.length === 4) {
            setTimeout(() => handleOtpVerifyClick(otpType), 300);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && otp.length === 4) {
            verifyOTP.current?.click();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text");
        const numericOnly = pastedText.replace(/\D/g, "").slice(0, 4);

        if (numericOnly) {
            setOtp(numericOnly);
            setOtpErrorMessage("");

            // Auto-submit if paste completes OTP and auto-submit is enabled
            if (enableAutoSubmit && numericOnly.length === 4) {
                setTimeout(() => handleOtpVerifyClick(otpType), 300);
            }
        }
    };

    const handleOtpVerifyClick = (type: string) => {
        if (otp.length === 4) {
            if (type === SIGNUP_STEPS.PHONEOTP) handlePhoneOtp(otp);
            else if (type === SIGNUP_STEPS.EMAILOTP) handleEmailOtp(otp);
        }
    };

    const handleResendOtp = () => {
        if (isResendEnabled) {
            setOtpErrorMessage("");
            setOtp("");
            resendOtp(otpType);
            setIsResendEnabled(false);
            setSeconds(30);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const handleClearOtp = () => {
        if (!buttonLoader) {
            setOtp("");
            setOtpErrorMessage("");
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    };

    const getMaskedNumber = (phone: string) => "*".repeat(phone.length - 5) + phone.slice(-5);

    const renderText = () =>
        otpType === SIGNUP_STEPS.PHONEOTP ? "Verify your mobile number" : "Verify your email";

    const getOtpHandle = () =>
        otpType === SIGNUP_STEPS.PHONEOTP ? getMaskedNumber(phoneNumber) : `${email}.`;

    useEffect(() => {
        if (otpErrorMessage) {
            const clearTimer = setTimeout(() => {
                setOtp(""); // clear OTP after 2 seconds
                inputRef.current?.focus();
            }, 2000);

            return () => clearTimeout(clearTimer);
        }
    }, [otpErrorMessage]);

    // Close mobile keyboard when OTP length reaches 4
    // useEffect(() => {
    //     if (otp.length === 4 && verifyOTP.current) {
    //         verifyOTP.current.focus();
    //     }
    // }, [otp]);

    return (
        <div className="flex min-h-full flex-col items-start">
            <p className="mb-9 mt-6 text-4xl font-semibold leading-[41.6px] tracking-[-0.48px] md:mb-8 md:mt-0">
                {renderText()}
            </p>
            <p
                className={`mb-9 text-sm font-regular leading-[22.4px] tracking-[0.14px] text-neutral-500 md:mb-8 md:text-base md:leading-[25.6px] md:text-neutral-900 ${
                    buttonLoader ? "pointer-events-none opacity-50" : ""
                }`}
            >
                {`Enter the OTP sent to ${getOtpHandle()}`}
                <span
                    className="ml-2 cursor-pointer text-neutral-500 underline md:text-neutral-900"
                    onClick={
                        buttonLoader
                            ? undefined
                            : otpType === SIGNUP_STEPS.PHONEOTP
                              ? editPhoneNumber
                              : otpType === SIGNUP_STEPS.EMAILOTP
                                ? editEmailId
                                : undefined
                    }
                >
                    {otpType === SIGNUP_STEPS.PHONEOTP
                        ? "Change number?"
                        : otpType === SIGNUP_STEPS.EMAILOTP
                          ? "Change email?"
                          : ""}
                </span>
            </p>

            {/* Single OTP Input */}
            <div className="relative mb-4 w-full">
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete={enableAutoRead ? "one-time-code" : "off"}
                    value={otp}
                    onChange={handleOtpChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={buttonLoader}
                    placeholder="Enter 4-digit OTP"
                    maxLength={4} // 4 digits
                    className={`h-[66px] w-full rounded-[12px] border px-4 text-left text-lg transition-all duration-200 ${
                        buttonLoader
                            ? "cursor-not-allowed border-gray-200 bg-gray-100 opacity-50"
                            : otpErrorMessage
                              ? "border-error-600 focus:border-error-600"
                              : isFocused
                                ? ""
                                : "border-gray-200 hover:border-gray-300"
                    } placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-neutral-300 focus:outline-none`}
                    aria-label="Enter 4-digit OTP"
                    aria-describedby={otpErrorMessage ? "otp-error" : undefined}
                />

                {/* Clear Button */}
                {otp.length > 0 && !buttonLoader && (
                    <button
                        onClick={handleClearOtp}
                        className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 transform items-center justify-center rounded-full bg-gray-200 transition-colors duration-200 hover:bg-gray-300"
                        aria-label="Clear OTP"
                        tabIndex={-1}
                    >
                        <span className="text-sm font-medium text-gray-600">×</span>
                    </button>
                )}
            </div>

            {/* Error Message */}
            <div
                className={`overflow-hidden transition-all duration-500 ${
                    otpErrorMessage ? "mb-2 max-h-10 opacity-100" : "mb-0 mt-0 max-h-0 opacity-0"
                }`}
            >
                <div className="flex items-start gap-2" id="otp-error">
                    <Info width="15" height="15" className="mt-1 text-error-600" />
                    <p className="text-sm text-error-600">{otpErrorMessage}</p>
                </div>
            </div>

            {/* Verify Button */}
            <RegularButton
                ref={verifyOTP}
                size={ButtonSize.Large}
                variant={ButtonVariant.PrimaryDefault}
                className="w-full text-lg transition-opacity duration-300 ease-in-out md:w-full md:relative md:bottom-auto md:mb-0 md:mt-4 min-w-full"
                noIcon={true}
                label={
                    buttonLoader ? (
                        <LoadingIcon className="m-auto h-5 w-5 animate-spin" />
                    ) : (
                        "Verify OTP"
                    )
                }
                showRightIcon={false}
                disabled={otp.length !== 4 || buttonLoader || !!otpErrorMessage}
                onClick={() => handleOtpVerifyClick(otpType)}
            />

            {/* Resend Timer */}
            <div className="mt-4 max-w-max md:mr-auto md:ml-0 ml-auto">
                <p>
                    {isResendEnabled ? (
                        <span
                            className={`text-sm underline ${
                                buttonLoader
                                    ? "cursor-not-allowed text-neutral-300"
                                    : "cursor-pointer text-brand-primary-500"
                            }`}
                            onClick={() => {
                                if (!buttonLoader) {
                                    handleResendOtp();
                                }
                            }}
                        >
                            Resend OTP
                        </span>
                    ) : (
                        <>
                            <span className="text-sm text-neutral-300">Resend OTP</span>{" "}
                            <span className="text-sm">in {seconds}s</span>
                        </>
                    )}
                </p>
            </div>

            <ConsentDisclaimer type={otpType} classnames="mt-8" />
        </div>
    );
};

export default OtpInput;